import {
  boolean,
  date,
  index,
  integer,
  jsonb,
  pgSchema,
  primaryKey,
  text,
  timestamp,
  unique,
  uniqueIndex,
  varchar,
} from "drizzle-orm/pg-core"
import { ulidId } from "./shared"

export const referenceSchema = pgSchema("reference")

export const refField = referenceSchema.table("field", {
  fieldUniqueId: varchar("field_unique_id", { length: 50 }).primaryKey(), // This is the 'field unique id' or 'fuid' for the field defn
  fieldName: text("field_name").notNull(), // Display name for field in TEO

  dataType: varchar("data_type", { length: 20 }), // Data type determines what additional attributes should be configured for this field

  apTransferSection: varchar("ap_transfer_section", { length: 30 }),
  apExportSection: varchar("ap_export_section", { length: 30 }),
  phytoExportSection: varchar("phyto_export_section", { length: 30 }),

  userConfigurable: boolean("user_configurable").notNull().default(false), // Only classifications are user editable
  classificationCode: varchar("classification_code", { length: 40 }).unique(),
  classificationName: varchar("classification_name", { length: 50 }).unique(),
  fieldCategory: varchar("field_category", {
    length: 5,
    enum: ["HFLD", "HRMK", "PFLD", "PRMK", "CLS", "NSI", "PLAB"],
  }),
  subCategory: varchar("sub_category", { length: 5 }),
  defaultDisplayType: varchar("default_display_type", {
    length: 20,
    enum: ["FREE_TEXT", "RESTRICTED_LIST", "UNRESTRICTED_LIST"],
  }),

  active: boolean("active").notNull().default(true),

  // AUDIT FIELDS
  createdAt: timestamp("created_at", { withTimezone: false })
    .defaultNow()
    .notNull(),
  createdBy: varchar("created_by"), // ID for user
  updatedAt: timestamp("updated_at", { withTimezone: false })
    .defaultNow()
    .notNull(),
  updatedBy: varchar("updated_by"), // ID for user
})

export const refCaption = referenceSchema.table("caption", {
  id: ulidId(), // prefix rcp
  fieldUniqueId: varchar("field_id", { length: 50 })
    .notNull()
    .references(() => refField.fieldUniqueId, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),

  text_en: text("text_en").notNull(),
  text_translations: jsonb("text_translations"),

  active: boolean("active").notNull().default(true),

  // AUDIT FIELDS
  createdAt: timestamp("created_at", { withTimezone: false })
    .defaultNow()
    .notNull(),
  createdBy: varchar("created_by"), // ID for user
  updatedAt: timestamp("updated_at", { withTimezone: false })
    .defaultNow()
    .notNull(),
  updatedBy: varchar("updated_by"), // ID for user
})

export const refList = referenceSchema.table("list", {
  listCode: varchar("list_code", { length: 40 }).primaryKey(), // e.g. "Country", "WineColour", "WineStyle"
  listName: text("list_name"),
  listDescription: text("list_desc"),
  listCategory: varchar("list_category", { length: 5 }),
  tableName: varchar("table_name", { length: 30 }).notNull(),

  // May be standalone reference data list or field specific
  fieldUniqueId: varchar("field_id", { length: 50 }).references(
    () => refField.fieldUniqueId,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  userConfigurable: boolean("user_configurable").notNull().default(false), // By default reference lists

  // AUDIT FIELDS
  createdAt: timestamp("created_at", { withTimezone: false })
    .defaultNow()
    .notNull(),
  createdBy: varchar("created_by"), // ID for user
  updatedAt: timestamp("updated_at", { withTimezone: false })
    .defaultNow()
    .notNull(),
  updatedBy: varchar("updated_by"), // ID for user
})

export const refItem = referenceSchema.table(
  "item",
  {
    id: integer().primaryKey().generatedByDefaultAsIdentity({ startWith: 1 }),
    listCode: varchar("list_code", { length: 40 })
      .notNull()
      .references(() => refList.listCode, {
        onDelete: "cascade",
        onUpdate: "cascade",
      }),

    itemCode: varchar("code", { length: 75 }).notNull(), // Backend/display code, must be unique within list. Increased to 75 to accommodate longer UN/CEFACT codes and commodity classifications
    itemName: text("name"), // Display description value (English)
    unCode: text("un_code"), // Where the code value for UN/CEFACT data exchange differs from the itemCode
    parentCode: varchar("parent_code", { length: 50 }), // Parent code for hierarchical reference data

    effectiveStartDate: date("effective_start_date").defaultNow().notNull(),
    effectiveEndDate: date("effective_end_date"),

    active: boolean("active").notNull().default(true),

    updatedAt: timestamp("updated_at", { withTimezone: false })
      .defaultNow()
      .notNull(),
    updatedBy: text("updated_by"),
  },
  (table) => {
    return {
      itemCode: uniqueIndex("idx_list_item_code") // Must always upsert records into a list using item code
        .on(table.listCode, table.itemCode),
    }
  },
)

export const refItemHistory = referenceSchema.table(
  "item_history",
  {
    id: integer().primaryKey().generatedByDefaultAsIdentity({ startWith: 1 }),
    itemId: integer("item_id").notNull(),
    operation: text("operation"),

    listCode: varchar("list_code", { length: 40 })
      .notNull()
      .references(() => refList.listCode, {
        onDelete: "cascade",
        onUpdate: "cascade",
      }),

    itemCode: varchar("code", { length: 75 }).notNull(), // Backend/display code, matches main item table length
    itemName: text("name"), // Display description value (English)
    unCode: text("un_code"), // Where the code value for UN/CEFACT data exchange differs from the itemCode

    effectiveStartDate: date("effective_start_date").defaultNow().notNull(),
    effectiveEndDate: date("effective_end_date"),

    updatedAt: timestamp("updated_at", { withTimezone: false })
      .defaultNow()
      .notNull(),
    updatedBy: text("updated_by"),
  },
  (table) => {
    return {
      itemCode: index("idx_list_item_code_history") // Must always upsert records into a list using item code
        .on(table.listCode, table.itemCode),
    }
  },
)

export const refCountry = referenceSchema.table("country", {
  id: integer().primaryKey().generatedByDefaultAsIdentity({ startWith: 1 }),

  itemCode: varchar("country_code", { length: 2 }).notNull().unique(), // Backend/display code
  itemName: text("country_name"), // Display description value (English)
  subregion: text("subregion"),
  region: text("region"),
  continent: text("continent"),
  capital: text("capital"),
  dial: text("dial"),
  short_name_en: text("short_name_en"),

  active: boolean("active").notNull().default(true),

  updatedAt: timestamp("updated_at", { withTimezone: false })
    .defaultNow()
    .notNull(),
  updatedBy: text("updated_by"),
})

export const refPort = referenceSchema.table("port", {
  id: integer().primaryKey().generatedByDefaultAsIdentity({ startWith: 1 }),

  countryId: integer("country_id")
    .notNull()
    .references(() => refCountry.id, { onDelete: "cascade" }),

  itemCode: varchar("port_code", { length: 6 }).notNull().unique(), // Backend/display code; UNLOCODE
  itemName: text("port_name"), // Display description value (English)
  nzHub: boolean("nz_hub"),

  active: boolean("active").notNull().default(true),

  updatedAt: timestamp("updated_at", { withTimezone: false })
    .defaultNow()
    .notNull(),
  updatedBy: text("updated_by"),
})

export const refLanguage = referenceSchema.table("language", {
  id: integer().primaryKey().generatedByDefaultAsIdentity({ startWith: 1 }),

  itemCode: varchar("language_code", { length: 3 }).notNull().unique(), // Backend/display code
  itemName: text("language_name"), // Display description value (English)
  rightToLeft: boolean("right_to_left"),

  active: boolean("active").notNull().default(true),

  updatedAt: timestamp("updated_at", { withTimezone: false })
    .defaultNow()
    .notNull(),
  updatedBy: text("updated_by"),
})

export const refCountryLanguage = referenceSchema.table(
  "country_language",
  {
    countryId: integer("country_id")
      .notNull()
      .references(() => refCountry.id, { onDelete: "cascade" }),
    languageId: integer("language_id")
      .notNull()
      .references(() => refLanguage.id, { onDelete: "cascade" }),

    updatedAt: timestamp("updated_at", { withTimezone: false })
      .defaultNow()
      .notNull(),
    updatedBy: text("updated_by"),
  },
  (table) => [primaryKey({ columns: [table.countryId, table.languageId] })],
)

export const refPortLanguage = referenceSchema.table(
  "port_language",
  {
    portId: integer("port_id")
      .notNull()
      .references(() => refPort.id, { onDelete: "cascade" }),
    languageId: integer("language_id")
      .notNull()
      .references(() => refLanguage.id, { onDelete: "cascade" }),

    updatedAt: timestamp("updated_at", { withTimezone: false })
      .defaultNow()
      .notNull(),
    updatedBy: text("updated_by"),
  },
  (table) => [primaryKey({ columns: [table.portId, table.languageId] })],
)

export const refUnit = referenceSchema.table(
  "unit",
  {
    id: integer().primaryKey().generatedByDefaultAsIdentity({ startWith: 1 }),

    listCode: varchar("list_code", {
      length: 15,
      // enum: ["unitWeight", "unitVolume", "unitOtherPhyto"],
    }).notNull(),

    unitCode: varchar("unit_code", { length: 3 }).notNull(), // e.g. KGM
    unitName: varchar("unit_name", { length: 20 }).notNull(), // e.g. Kilogram
    unitSymbol: varchar("symbol", { length: 10 }), // e.g. kg

    active: boolean("active").notNull().default(true),

    updatedAt: timestamp("updated_at", { withTimezone: false })
      .defaultNow()
      .notNull(),
    updatedBy: text("updated_by"),
  },
  (table) => {
    return {
      unitCode: uniqueIndex("idx_unit_list_item_code") // Must always upsert records into a list using unit code
        .on(table.listCode, table.unitCode),
    }
  },
)

export const refProcess = referenceSchema.table("process", {
  id: integer().primaryKey().generatedByDefaultAsIdentity({ startWith: 1 }),

  processCode: varchar("process_code", { length: 3 }).notNull().unique(), // Backend/display code
  processName: text("process_name"), // Display description value (English)
  unCode: text("un_code"), // Code value for UN/CEFACT data exchange

  mostCommon: boolean("most_common").default(false),
  ranking: integer("ranking"),
  commodityAp: varchar("commodity_ap", { length: 50 }),
  commodityPhyto: varchar("commodity_phyto", { length: 50 }),
  // commodities: TODO define commodity or commodity group to restrict selection for 'get_available_processes'

  active: boolean("active").notNull().default(true),

  updatedAt: timestamp("updated_at", { withTimezone: false })
    .defaultNow()
    .notNull(),
  updatedBy: text("updated_by"),
})

export const refProcessGroup = referenceSchema.table("process_group", {
  id: integer().primaryKey().generatedByDefaultAsIdentity({ startWith: 1 }),

  groupName: varchar("group_name", { length: 50 }).notNull().unique(),
  groupType: varchar("group_type", { enum: ["DATE", "PREMISES"] }).notNull(),
  overrideUNCode: varchar("override_un_code", { length: 10 }),
  overrideCharacteristic: varchar("override_characteristic", { length: 50 }),

  active: boolean("active").notNull().default(true),

  updatedAt: timestamp("updated_at", { withTimezone: false })
    .defaultNow()
    .notNull(),
  updatedBy: text("updated_by"),
})

export const refProcessGroupAssign = referenceSchema.table(
  "process_group_assign",
  {
    processGroupId: integer("process_group_id")
      .notNull()
      .references(() => refProcessGroup.id, { onDelete: "cascade" }),
    processId: integer("process_id")
      .notNull()
      .references(() => refProcess.id, { onDelete: "cascade" }),

    active: boolean("active").notNull().default(true),

    updatedAt: timestamp("updated_at", { withTimezone: false })
      .defaultNow()
      .notNull(),
    updatedBy: text("updated_by"),
  },
  (table) => [primaryKey({ columns: [table.processGroupId, table.processId] })],
)

export const refProcessGroupCaption = referenceSchema.table(
  "process_group_caption",
  {
    id: integer().primaryKey().generatedByDefaultAsIdentity({ startWith: 1 }), // TODO agree if this should be int to match iText or ulid to match field caption.
    processGroupId: integer("process_group_id")
      .notNull()
      .references(() => refProcessGroup.id, { onDelete: "cascade" }),

    text_en: text("text_en").notNull(),
    text_translations: jsonb("text_translations"),

    active: boolean("active").notNull().default(true),

    // AUDIT FIELDS
    createdAt: timestamp("created_at", { withTimezone: false })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by"), // ID for user
    updatedAt: timestamp("updated_at", { withTimezone: false })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by"), // ID for user
  },
)

export const refPhytoBotanicalName = referenceSchema.table(
  "phyto_botanical_name",
  {
    id: integer().primaryKey().generatedByDefaultAsIdentity({ startWith: 1 }),

    botanicalName: varchar("botanical_name").notNull(),
    commonName: varchar("common_name"),

    effectiveStartDate: date("effective_start_date").defaultNow().notNull(),
    effectiveEndDate: date("effective_end_date"),

    active: boolean("active").notNull().default(true),

    updatedAt: timestamp("updated_at", { withTimezone: false })
      .defaultNow()
      .notNull(),
    updatedBy: text("updated_by"),
  },
  (table) => {
    return {
      itemCode: unique("idx_botanical_names") // Combo of botanical and common name must be unique
        .on(table.botanicalName, table.commonName)
        .nullsNotDistinct(),
    }
  },
)

export const refEUIngredient = referenceSchema.table(
  "eu_ingredient",
  {
    id: integer().primaryKey().generatedByDefaultAsIdentity({ startWith: 1 }),

    ingredient: text("ingredient").notNull(),
    functionalCategory: text("functional_category"),
    eNumber: text("e_number"),
    allergen: boolean("allergen").default(false),
    priority: integer("priority"),

    active: boolean("active").notNull().default(true),

    updatedAt: timestamp("updated_at", { withTimezone: false })
      .defaultNow()
      .notNull(),
    updatedBy: text("updated_by"),
  },
  (table) => [
    unique("idx_unique_ingredient_functional_category")
      .on(table.ingredient, table.functionalCategory)
      .nullsNotDistinct(),
  ],
)

export const refTypeAuditEvent = referenceSchema.table(
  "ref_type_audit_event",
  {
    id: varchar("id", { length: 30 }).primaryKey(), // format: rae-{ulid}
    refType: varchar("ref_type", { length: 50 }).notNull(),
    refItemId: varchar("ref_item_id", { length: 75 }).notNull(),
    refItemName: varchar("ref_item_name", { length: 255 }),
    eventType: varchar("event_type", { length: 20 }).notNull(), // 'CREATED', 'DELETED', 'UPDATED'
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by", { length: 255 }).notNull(),
  },
  (table) => ({
    refTypeIdx: index("idx_ref_type_audit_ref_type").on(table.refType),
    refItemIdx: index("idx_ref_type_audit_ref_item").on(
      table.refType,
      table.refItemId,
    ),
    createdAtIdx: index("idx_ref_type_audit_created_at").on(table.createdAt),
  }),
)
